import Big from 'big.js'
import { WithTooltip } from 'metronome-ui/components/WithTooltip'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'
import { useEffect, useState } from 'react'

import usePositionHealth from '../hooks/usePositionHealth'
import { usePrevious } from '../hooks/usePrevious'

import Counter from './Counter'
import FiatNumber from './FiatNumber'
import { HealthFactorModal } from './HealthFactorModal'
import IconContainer from './icons/IconContainer'
import {
  OperationDetailsItem,
  OperationDetailsList,
  OperationDetailsPreviewItem
} from './OperationDetails'
import PositionHealthBarReview from './PositionHealthBarDetail'

export const OperationDetailsPreviewPosition = function ({
  className,
  debtInUsd,
  issuableLimitInUsd,
  projectedDebtInUsd,
  projectedIssuableLimitInUsd,
  onPositionHealthChange,
  showHealthFactor = false
}) {
  const { t } = useTranslation()
  const [showModal, setShowModal] = useState(false)

  const { color, healthFactor, percent } = usePositionHealth({
    debtInUsd,
    issuableLimitInUsd
  })
  const {
    color: nextColor,
    healthFactor: nextHealthFactor,
    healthFactorGradient: nextHealthFactorGradient,
    health: nextHealth,
    percent: nextPercent,
    getColor: nextGetColor
  } = usePositionHealth({
    debtInUsd: projectedDebtInUsd,
    issuableLimitInUsd: projectedIssuableLimitInUsd
  })
  const previousNextPercent = usePrevious(nextPercent)

  useEffect(
    function () {
      onPositionHealthChange?.(nextHealth)
    },
    [nextHealth, onPositionHealthChange]
  )

  return (
    <>
      <div
        className={`rounded-xl px-4 py-4 text-sm ${className ? className : ''}`}
      >
        <OperationDetailsPreviewItem
          className="pt-0 text-xs text-gray-500"
          currentValue={percent}
          nextValue={
            <Counter
              decimals={1}
              end={nextPercent}
              start={previousNextPercent}
              suffix="%"
            />
          }
          title={
            <>
              {t('synth-limit')}
              <WithTooltip
                id="tip-status-block-detail"
                overlay={
                  <div className="whitespace-normal">
                    <p>{t('status-block-detail.tooltip')}</p>
                  </div>
                }
              >
                <IconContainer className="ml-1 inline" name="exclamationmark" />
              </WithTooltip>
            </>
          }
        />
        {showHealthFactor && (
          <OperationDetailsPreviewItem
            className="pt-0 text-xs text-gray-500"
            currentValue={
              <span
                style={{
                  color: color === 'transparent' ? 'inherit' : color
                }}
              >
                {healthFactor || 0}
              </span>
            }
            nextValue={
              <span
                style={{
                  color: nextColor === 'transparent' ? 'inherit' : nextColor
                }}
              >
                {nextHealthFactor || 0}
              </span>
            }
            title={
              <span className="capitalize">
                {t('health-factor')}
                <IconContainer
                  className="ml-1 inline cursor-pointer"
                  name="exclamationmark"
                  onClick={() => setShowModal(true)}
                />
              </span>
            }
          />
        )}
        <PositionHealthBarReview health={nextHealth} percent={nextPercent} />
      </div>
      <HealthFactorModal
        color={nextColor}
        end={75}
        getColor={nextGetColor}
        healthFactor={nextHealthFactor}
        healthFactorGradient={nextHealthFactorGradient}
        modalIsOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        percent={nextPercent}
        start={9}
      />
    </>
  )
}

// eslint-disable-next-line complexity
const SynthOperationDetails = function ({
  collateralFactor,
  debtInUsd,
  depositInUsd,
  fee,
  generatedInUsd,
  generatedInUsdNextAmount,
  generatedAssetSymbol,
  inputValue = 0,
  interestRate,
  issuableLimitInUsd,
  nextDebtInUsd,
  nextIssuableLimitInUsd,
  onPositionHealthChange,
  totalDepositedNextAmount
}) {
  const { t } = useTranslation()
  const feeAmount = Big(fee).times(inputValue)

  const showLiquidationPoint =
    issuableLimitInUsd !== undefined && nextIssuableLimitInUsd !== undefined

  return (
    <div className="bg-blue-secondary rounded-lg p-5">
      <h2 className="mb-4 text-xl text-white">{t('review')}</h2>
      <div className="bg-blue-tertiary rounded-lg">
        {!(
          typeof nextDebtInUsd === 'undefined' &&
          typeof nextIssuableLimitInUsd === 'undefined'
        ) && (
          <OperationDetailsPreviewPosition
            debtInUsd={debtInUsd}
            issuableLimitInUsd={issuableLimitInUsd}
            onPositionHealthChange={onPositionHealthChange}
            projectedDebtInUsd={nextDebtInUsd}
            projectedIssuableLimitInUsd={nextIssuableLimitInUsd}
          />
        )}
        <OperationDetailsList>
          {typeof depositInUsd !== 'undefined' && (
            <OperationDetailsPreviewItem
              currentValue={
                <FiatNumber value={Big(depositInUsd || 0).toFixed(2)} />
              }
              nextValue={
                <FiatNumber
                  value={Big(totalDepositedNextAmount || 0).toFixed(2)}
                />
              }
              title={t('operation-details.deposited-collateral')}
            />
          )}
          {collateralFactor !== undefined && (
            <OperationDetailsItem
              title={t('operation-details.collateral-factor')}
            >
              <div className="flex">{collateralFactor}%</div>
            </OperationDetailsItem>
          )}
          {interestRate !== undefined && (
            <OperationDetailsItem title={t('synth-balance-fee')}>
              <div className="flex">{interestRate}%</div>
            </OperationDetailsItem>
          )}
          {showLiquidationPoint &&
            (issuableLimitInUsd === nextIssuableLimitInUsd ? (
              <OperationDetailsItem
                title={t('operation-details.liquidation-point')}
                tooltip={t('operation-details.liquidation-point-description')}
              >
                <FiatNumber value={issuableLimitInUsd || 0} />
              </OperationDetailsItem>
            ) : (
              <OperationDetailsPreviewItem
                currentValue={<FiatNumber value={issuableLimitInUsd || 0} />}
                nextValue={<FiatNumber value={nextIssuableLimitInUsd || 0} />}
                title={t('operation-details.liquidation-point')}
              />
            ))}
          {generatedInUsd && generatedInUsdNextAmount ? (
            <OperationDetailsPreviewItem
              currentValue={<FiatNumber value={generatedInUsd || 0} />}
              nextValue={<FiatNumber value={generatedInUsdNextAmount || 0} />}
              title={
                <Trans
                  components={[
                    <span className="mr-1 normal-case" key="generated-asset" />
                  ]}
                  i18nKey="operation-details.generated-asset"
                  values={{ symbol: generatedAssetSymbol }}
                />
              }
            />
          ) : null}
          {Big(fee).gt(0) && (
            <OperationDetailsItem title={t('operation-details.operation-fee')}>
              <div className="flex">
                <FiatNumber value={feeAmount} />
                <span className="ml-1">
                  ({Big(fee).times(100).toNumber()}%)
                </span>
              </div>
            </OperationDetailsItem>
          )}
        </OperationDetailsList>
      </div>
    </div>
  )
}

export default SynthOperationDetails
